import { memo, useLayoutEffect, useRef, useState } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { formatForeignCurrency } from "@common/helpers/currency";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { MonetaryInput } from "@design-system/MonetaryInput/MonetaryInput";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import {
    CatalogItemPriceRow,
    CatalogItemPricesTableCallbacks,
    CatalogItemPricesViewOptions,
} from "../catalogItemPrices.types";

function DraftPriceRateCell({
    isPending,
    shouldFocus,
    onCommit,
    onCancel,
}: {
    isPending: boolean;
    shouldFocus: boolean;
    onCommit: (unitAmount: number) => Promise<void>;
    onCancel: () => void;
}) {
    const [unitAmount, setUnitAmount] = useState<number | undefined>(undefined);
    const inputContainerRef = useRef<HTMLDivElement>(null);
    const isSavingRef = useRef(false);
    // Latches once a create succeeds and never resets, so a blur fired while the
    // draft row unmounts after a successful commit cannot create a duplicate
    // price. `isSavingRef` only covers the in-flight window, not the unmount.
    const hasCommittedRef = useRef(false);

    useLayoutEffect(
        function focusDraftAmountInput() {
            if (!shouldFocus) {
                return;
            }
            inputContainerRef.current?.querySelector("input")?.focus();
        },
        [shouldFocus],
    );

    const commit = async () => {
        if (isSavingRef.current || isPending || hasCommittedRef.current) {
            return;
        }
        if (unitAmount === undefined || !Number.isFinite(unitAmount)) {
            onCancel();
            return;
        }
        isSavingRef.current = true;
        try {
            await onCommit(unitAmount);
            hasCommittedRef.current = true;
        } finally {
            isSavingRef.current = false;
        }
    };

    return (
        <ViewTableCell captureClicks>
            <div
                ref={inputContainerRef}
                onMouseDown={(event) => event.stopPropagation()}
                onKeyDown={(event) => {
                    if (event.key === "Enter") {
                        event.preventDefault();
                        event.stopPropagation();
                        void commit();
                    } else if (event.key === "Escape") {
                        event.preventDefault();
                        event.stopPropagation();
                        onCancel();
                    }
                }}
            >
                <MonetaryInput
                    size="xs"
                    value={unitAmount}
                    showCurrencySymbolIfEmpty
                    allowNegative={false}
                    selectOnFocus
                    disabled={isPending}
                    onValueChange={setUnitAmount}
                    onBlur={() => {
                        void commit();
                    }}
                />
            </div>
        </ViewTableCell>
    );
}

export const PriceRateCell = memo(function PriceRateCell({
    row,
    table,
}: CellContext<ViewTableFeatures, CatalogItemPriceRow, unknown>) {
    const { callbacks, options } = useViewTableMeta<
        CatalogItemPricesTableCallbacks,
        CatalogItemPricesViewOptions
    >(table);
    const price = row.original;
    if (price.isDraft && callbacks) {
        return (
            <DraftPriceRateCell
                isPending={options?.isDraftPending ?? false}
                shouldFocus={options?.shouldFocusDraft ?? false}
                onCommit={callbacks.onCommitDraft}
                onCancel={callbacks.onCancelDraft}
            />
        );
    }
    return (
        <ViewTableCell>
            {formatForeignCurrency(
                price.unitAmount,
                price.currency.toUpperCase(),
            )}
        </ViewTableCell>
    );
});
